import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  TextInput,
  Image,
  ActivityIndicator,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { getRewardsCatalog, getUserRewards, formatRewardForDisplay, formatCategoryForDisplay } from '../services/rewardsApi';
import SharedHeader from '../components/SharedHeader';
import TabNavigation from '../components/TabNavigation';
import SearchFilter from '../components/SearchFilter';

const { width, height } = Dimensions.get('window');

export default function RewardsScreen({ navigation }) {
  const [activeTab, setActiveTab] = useState('myRewards');
  const [searchQuery, setSearchQuery] = useState('');
  const [userData, setUserData] = useState(null);
  const [myRewards, setMyRewards] = useState([]);
  const [rewardCatalog, setRewardCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchantExchanges, setMerchantExchanges] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  useEffect(() => {
    loadUserData();
    loadRewardsData();
  }, []);

  useEffect(() => {
    if (activeTab === 'catalog') {
      loadRewardsCatalog();
    } else if (activeTab === 'myRewards') {
      loadUserRewards();
    }
  }, [activeTab, searchQuery]);

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('session_id');
      const userDataString = await AsyncStorage.getItem('userData');
      
      if (userToken && sessionId && userDataString) {
        setUserData(JSON.parse(userDataString));
      } else {
        // If no authentication, redirect to login
        navigation.navigate('Login');
      }
    } catch (error) {
      console.log('Error loading user data:', error);
      navigation.navigate('Login');
    }
  };

  const loadRewardsData = () => {
    // Mock data for Merchant Exchanges (keeping this as fallback)
    const merchantData = [
      {
        id: 1,
        merchantName: 'Starbucks',
        title: 'Free Coffee',
        description: 'Any size coffee drink',
        pointsCost: 120,
        category: 'Food & Beverage',
        logo: 'https://via.placeholder.com/50x50/00704A/FFFFFF?text=SB',
        availability: 'Available',
      },
      {
        id: 2,
        merchantName: 'McDonald\'s',
        title: 'Big Mac Meal',
        description: 'Big Mac with fries and drink',
        pointsCost: 180,
        category: 'Food & Beverage',
        logo: 'https://via.placeholder.com/50x50/FFC72C/FFFFFF?text=M',
        availability: 'Available',
      },
      {
        id: 3,
        merchantName: 'Uniqlo',
        title: '15% Off Coupon',
        description: 'Discount on any purchase',
        pointsCost: 90,
        category: 'Fashion',
        logo: 'https://via.placeholder.com/50x50/FF0000/FFFFFF?text=U',
        availability: 'Limited',
      },
    ];

    setMerchantExchanges(merchantData);
  };

  const loadRewardsCatalog = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getRewardsCatalog(page, 10, searchQuery);
      
      if (response.code === 200) {
        const formattedRewards = response.data.rewards.map(formatRewardForDisplay);
        const formattedCategories = response.data.categories.map(formatCategoryForDisplay);
        
        if (page === 1) {
          setRewardCatalog(formattedRewards);
        } else {
          setRewardCatalog(prev => [...prev, ...formattedRewards]);
        }
        
        setCategories(formattedCategories);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading rewards catalog:', error);
      Alert.alert('Error', 'Failed to load rewards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUserRewards = async () => {
    try {
      setLoading(true);
      const response = await getUserRewards();
      
      if (response.code === 200) {
        const formattedRewards = response.data.rewards.map(formatRewardForDisplay);
        setMyRewards(formattedRewards);
      }
    } catch (error) {
      console.error('Error loading user rewards:', error);
      // For now, keep empty array as user rewards endpoint is not implemented
      setMyRewards([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (pagination.has_next && !loading && activeTab === 'catalog') {
      loadRewardsCatalog(pagination.current_page + 1);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Reset pagination when searching
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const tabs = [
    { key: 'myRewards', label: 'Reward Saya' },
    { key: 'catalog', label: 'Katalog Reward' },
    { key: 'merchant', label: 'Tukar Merchant' },
  ];

  const renderMyRewardCard = (reward) => (
    <View key={reward.id} style={styles.RewardCard}>
      <View style={styles.ImageContainer}>
        <Image source={reward.image || require('../../assets/rewards/reward-image-1.png')} style={styles.RewardImage} resizeMode="cover" />
      </View>
      <View style={styles.TextContainer}>
        <View style={styles.TopSection}>
          <TouchableOpacity style={[
            styles.StockButton,
            reward.status === 'Active' ? { backgroundColor: '#E8F5E8' } : { backgroundColor: '#FFF3E0' }
          ]}>
            <Image 
              source={require('../../assets/icons/ticket-icon.png')} 
              style={{
                width: 12, 
                height: 12, 
                tintColor: reward.status === 'Active' ? '#4CAF50' : '#FF9800'
              }} 
              resizeMode="contain"
            />
            <Text style={[
              styles.StockText,
              { color: reward.status === 'Active' ? '#4CAF50' : '#FF9800' }
            ]}>{reward.status}</Text>
          </TouchableOpacity>
          <Text style={styles.RewardTitle}>{reward.title}</Text>
        </View>
        <TouchableOpacity style={styles.ExchangeButton}>
          <Text style={styles.ExchangeButtonText}>Gunakan</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderCatalogCard = (reward) => (
    <View key={reward.id} style={styles.RewardCard}>
      <View style={styles.ImageContainer}>
        <Image source={reward.image} style={styles.RewardImage} resizeMode="cover" />
      </View>
      <View style={styles.TextContainer}>
        <View style={styles.TopSection}>
          <TouchableOpacity style={styles.StockButton}>
            <Image 
              source={require('../../assets/icons/ticket-icon.png')} 
              style={{width: 12, height: 12, tintColor: '#4A90E2'}} 
              resizeMode="contain"
            />
            <Text style={styles.StockText}>{reward.availability}</Text>
          </TouchableOpacity>
          <Text style={styles.RewardTitle}>{reward.title}</Text>
          <View style={styles.PointContainer}>
            <View style={styles.PointBadge}>
              <Ionicons name="star" size={12} color="#FFD700" />
            </View>
            <Text style={styles.PointText}>{reward.pointsCost}</Text>
          </View>
        </View>
        <TouchableOpacity style={styles.ExchangeButton}>
          <Text style={styles.ExchangeButtonText}>Tukar</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderMerchantCard = (merchant) => (
    <TouchableOpacity key={merchant.id} style={styles.merchantCard}>
      <View style={styles.merchantHeader}>
        <View style={styles.merchantLogo}>
          <Text style={styles.merchantLogoText}>
            {merchant.merchantName.charAt(0)}
          </Text>
        </View>
        <View style={styles.merchantInfo}>
          <Text style={styles.merchantName}>{merchant.merchantName}</Text>
          <Text style={styles.merchantTitle}>{merchant.title}</Text>
        </View>
        <Text style={styles.merchantPoints}>{merchant.pointsCost} pts</Text>
      </View>
      <Text style={styles.merchantDescription}>{merchant.description}</Text>
      <View style={styles.merchantFooter}>
        <Text style={styles.merchantCategory}>{merchant.category}</Text>
        <Text style={[
          styles.merchantAvailability,
          merchant.availability === 'Available' ? styles.availableText : styles.limitedText,
        ]}>
          {merchant.availability}
        </Text>
      </View>
    </TouchableOpacity>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'myRewards':
        return (
          <View style={styles.contentContainer}>
            {myRewards.length > 0 ? (
              <View style={styles.RewardsGrid}>
                {myRewards.map(renderMyRewardCard)}
              </View>
            ) : !loading ? (
              <View style={styles.emptyState}>
                <Ionicons name="gift-outline" size={64} color="#E0E0E0" />
                <Text style={styles.emptyStateText}>No rewards yet</Text>
                <Text style={styles.emptyStateSubtext}>Start earning points to get rewards!</Text>
              </View>
            ) : null}
          </View>
        );
      case 'catalog':
        return (
          <View style={styles.contentContainer}>
            {rewardCatalog.length > 0 ? (
              <View style={styles.RewardsGrid}>
                {rewardCatalog.map(renderCatalogCard)}
              </View>
            ) : !loading ? (
              <View style={styles.emptyState}>
                <Ionicons name="gift-outline" size={64} color="#E0E0E0" />
                <Text style={styles.emptyStateText}>No rewards found</Text>
                <Text style={styles.emptyStateSubtext}>Try adjusting your search or check back later</Text>
              </View>
            ) : null}
          </View>
        );
      case 'merchant':
        return (
          <View style={styles.contentContainer}>
            {merchantExchanges.map(renderMerchantCard)}
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <SharedHeader 
        title="Rewards"
        onBackPress={() => navigation.goBack()}
        onNotificationPress={() => {/* Handle notification press */}}
      />

      <TabNavigation 
        tabs={tabs}
        activeTab={activeTab}
        onTabPress={setActiveTab}
      />

      <SearchFilter 
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        placeholder="Cari voucher disini"
        onFilterPress={() => {/* Handle filter press */}}
      />

      <ScrollView 
        style={styles.scrollContainer} 
        showsVerticalScrollIndicator={false}
        onScroll={({ nativeEvent }) => {
          const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
          const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;
          if (isCloseToBottom) {
            handleLoadMore();
          }
        }}
        scrollEventThrottle={400}
      >
        {renderContent()}
        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#4A90E2" />
            <Text style={styles.loadingText}>Loading rewards...</Text>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },


  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 120,
  },
  // My Rewards styles
  rewardCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    boxShadow: '0px 2px 3.84px rgba(0, 0, 0, 0.1)',
    elevation: 5,
  },
  expiredCard: {
    opacity: 0.6,
  },
  rewardCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  rewardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  activeBadge: {
    backgroundColor: '#E8F5E8',
  },
  expiredBadge: {
    backgroundColor: '#FFF3E0',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
  },
  activeStatusText: {
    color: '#4CAF50',
  },
  expiredStatusText: {
    color: '#FF9800',
  },
  rewardDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  rewardCode: {
    fontSize: 14,
    fontWeight: '500',
    color: '#4A90E2',
    marginBottom: 4,
  },
  rewardType: {
    fontSize: 12,
    color: '#999',
  },
  //  Reward Card Styles
  RewardsList: {
    flexDirection: 'column',
  },
  RewardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  RewardCard: {
    backgroundColor: 'white',
    borderRadius: 8,
    marginBottom: 16,
    width: (width - 60) / 2,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
    overflow: 'hidden',
  },
  ImageContainer: {
    height: 120,
    backgroundColor: '#F5F5F5',
  },
  RewardImage: {
    width: '100%',
    height: '100%',
  },
  TextContainer: {
    padding: 16,
  },
  TopSection: {
    marginBottom: 12,
  },
  StockButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  StockText: {
    fontSize: 10,
    color: '#4A90E2',
    fontWeight: '500',
    marginLeft: 4,
  },
  RewardTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
    lineHeight: 18,
  },
  PointContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  PointBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#FFF8E1',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 6,
    borderWidth: 1,
    borderColor: '#FFD700',
  },
  PointText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFB300',
  },
  ExchangeButton: {
    backgroundColor: 'black',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  ExchangeButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: 'white',
  },
  // Legacy Catalog styles (keeping for compatibility)
  catalogGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  catalogCard: {
    width: (width - 60) / 2,
    backgroundColor: 'white',
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: '0px 2px 3.84px rgba(0, 0, 0, 0.1)',
    elevation: 5,
  },
  catalogCardHeader: {
    height: 80,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  catalogCardContent: {
    padding: 12,
  },
  catalogTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  catalogDescription: {
    fontSize: 12,
    color: '#666',
    marginBottom: 8,
  },
  catalogFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pointsCost: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#4A90E2',
  },
  availability: {
    fontSize: 10,
    fontWeight: '500',
  },
  availableText: {
    color: '#4CAF50',
  },
  limitedText: {
    color: '#FF9800',
  },
  // Merchant styles
  merchantCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    boxShadow: '0px 2px 3.84px rgba(0, 0, 0, 0.1)',
    elevation: 5,
  },
  merchantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  merchantLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  merchantLogoText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
  },
  merchantInfo: {
    flex: 1,
  },
  merchantName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  merchantTitle: {
    fontSize: 12,
    color: '#666',
  },
  merchantPoints: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#4A90E2',
  },
  merchantDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  merchantFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  merchantCategory: {
    fontSize: 12,
    color: '#999',
  },
  merchantAvailability: {
    fontSize: 12,
    fontWeight: '500',
  },
  // Empty state styles
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#666',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  // Loading styles
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
  },
});